import './styles/Popover.scss';

import { AnimatePresence, motion } from 'motion/react';
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { useClassHelper, useOutsideClick } from '../../hooks';

type Position = 'left' | 'right';

interface IRenderProps {
  isOpen: boolean;
  handleClose: () => void;
}

interface PortalPosition {
  top: number;
  left: number;
  right: number;
}

export interface PopoverProps {
  trigger: ({ isOpen }: Pick<IRenderProps, 'isOpen'>) => ReactNode;
  content: ({ isOpen, handleClose }: IRenderProps) => ReactNode;
  id: string;
  defaultPosition?: Position;
  withPadding?: boolean;
  disabled?: boolean;
  /**
   * Renders the panel through a portal into `document.body`, fixed-positioned
   * from the trigger's viewport rect. Use it when the trigger sits inside a
   * scroll or clipping container (`overflow-auto`, `overflow-hidden`): an
   * in-flow panel there either grows the container a scrollbar or is cut off.
   */
  isPortaled?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  classNames?: {
    wrapper?: string;
    trigger?: string;
    content?: string;
  };
}

const componentName = 'c-Popover';

// Above Modal (100) and Tooltip (50): a portaled panel may be opened from
// inside a modal and must not be buried under it. Same layer as the Select's
// own option list and the minimized SideMenu.
const PORTAL_Z_INDEX = 1000;

const measurePortalPosition = (trigger: HTMLElement): PortalPosition => {
  const rect = trigger.getBoundingClientRect();

  // Fixed `right` is measured from the layout viewport, which excludes a
  // classic (non-overlay) scrollbar; window.innerWidth includes it and would
  // push the panel under the bar on Windows.
  return {
    top: rect.bottom,
    left: rect.left,
    right: document.documentElement.clientWidth - rect.right,
  };
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Popover component that shows a content overlay when triggered.
 */
export const Popover = ({
  trigger,
  content,
  id,
  defaultPosition = 'right',
  withPadding,
  disabled,
  isPortaled = false,
  onClose,
  onOpen,
  classNames,
}: PopoverProps) => {
  const c = useClassHelper(componentName);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<Position>(defaultPosition);
  const [portalPosition, setPortalPosition] = useState<PortalPosition | null>(
    null,
  );
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleSetIsOpen = (value: boolean) => {
    if (value) {
      onOpen?.();
      updatePosition();

      if (isPortaled && triggerRef.current) {
        setPortalPosition(measurePortalPosition(triggerRef.current));
      }

      // Reopening mid-exit can revive the same element, still inert from
      // the Tab hand-off below.
      popoverRef.current?.removeAttribute('inert');
    } else {
      if (isOpen) {
        onClose?.();
      }
    }

    setIsOpen(value);
  };

  const handleClose = () => {
    handleSetIsOpen(false);
  };

  /**
   * Handles clicks outside of the popover and trigger elements to close the popover.
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (triggerRef.current?.contains(event.target as Node)) {
      return; // Do nothing if the click is inside the trigger element
    }

    handleSetIsOpen(false);
  };

  /**
   * Handles keydown events to close the popover when the Escape key is pressed.
   */
  useOutsideClick(popoverRef, handleClickOutside);

  /**
   * Updates the position of the popover to ensure it stays within the viewport.
   */
  const updatePosition = () => {
    if (popoverRef.current && triggerRef.current) {
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      if (popoverRect.right > viewportWidth) {
        setPosition('left');
      }
    }
  };

  const getPanelFocusables = () => {
    return Array.from(
      popoverRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ??
        [],
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleSetIsOpen(false);
      return;
    }

    // A portaled panel sits at the end of <body>, so Tab from the trigger
    // would skip it; hand focus over the way an in-flow panel receives it.
    if (isPortaled && isOpen && event.key === 'Tab' && !event.shiftKey) {
      const [first] = getPanelFocusables();

      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const handlePanelKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleSetIsOpen(false);
      triggerRef.current?.focus();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusables = getPanelFocusables();

    if (event.shiftKey && document.activeElement === focusables[0]) {
      event.preventDefault();
      triggerRef.current?.focus();
    } else if (
      !event.shiftKey &&
      document.activeElement === focusables[focusables.length - 1]
    ) {
      // Deliberately not prevented: the browser's Tab then moves on from the
      // trigger, landing where it would after an in-flow panel. The panel
      // stays in the DOM, at the end of <body>, while its exit animation runs;
      // inert keeps that Tab from landing back inside it when nothing
      // focusable follows the trigger. Set on the node because AnimatePresence
      // renders the exiting element with its last props.
      popoverRef.current?.setAttribute('inert', '');
      triggerRef.current?.focus();
      handleSetIsOpen(false);
    }
  };

  useEffect(() => {
    updatePosition();
  }, []);

  // In portal mode the panel is no longer a flow sibling of the trigger, so it
  // has to follow the trigger while any ancestor scrolls or the window resizes.
  useLayoutEffect(() => {
    if (!isPortaled || !isOpen || !triggerRef.current) {
      return undefined;
    }

    const trigger = triggerRef.current;
    const follow = () => {
      setPortalPosition(measurePortalPosition(trigger));
    };

    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);

    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [isPortaled, isOpen]);

  const getPortalStyle = (): CSSProperties | undefined => {
    if (!isPortaled || !portalPosition) {
      return undefined;
    }

    const horizontal: CSSProperties =
      position === 'left'
        ? { right: portalPosition.right, left: 'auto' }
        : { left: portalPosition.left, right: 'auto' };

    return {
      position: 'fixed',
      top: portalPosition.top,
      zIndex: PORTAL_Z_INDEX,
      ...horizontal,
    };
  };

  const panel = (
    <>
      {/*
        motion v12 reads `ref` from props (React 19 pattern).
        React 18.3 warns about this via AnimatePresence → PopChild internals.
        Warning disappears on React 19 upgrade.
      */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1, transition: { duration: 0.1 } }}
            exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.1 } }}
            ref={popoverRef}
            id={id}
            className={`${c('__content', '__content--open', {
              '__content--padding': withPadding,
            })} ${position} ${classNames?.content || ''}`}
            style={getPortalStyle()}
            onKeyDown={isPortaled ? handlePanelKeyDown : undefined}
            role="dialog"
            aria-labelledby={`${id}-header`}
            aria-describedby={`${id}-body`}
            tabIndex={-1}
          >
            {content({ isOpen, handleClose })}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <div className={`${componentName} ${classNames?.wrapper || ''}`}>
      <button
        ref={triggerRef}
        onClick={() => handleSetIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={id}
        className={`${c('__trigger')} focus-visible:focus-ring ${
          classNames?.trigger || ''
        }`}
        onKeyDown={handleKeyDown}
        type="button"
        aria-disabled={disabled ? 'true' : 'false'}
        disabled={disabled}
      >
        {trigger({ isOpen })}
      </button>
      {isPortaled ? createPortal(panel, document.body) : panel}
    </div>
  );
};
